<template>
  <div class="record-practice-page">
    <!-- 头部 -->
    <div class="header">
      <button class="back-btn" @click="goBack">
        <span class="back-icon">←</span>
      </button>
      <h1 class="page-title">发音练习</h1>
    </div>

    <!-- 单词展示卡片 -->
    <div class="word-display-card">
      <div class="word-image">{{ wordIcon }}</div>
      <h2 class="target-word">{{ word.word }}</h2>
      <p class="word-phonetic">{{ word.phonetic }}</p>
      <p class="word-meaning">{{ word.meaning }}</p>
      <button class="play-sample-btn" @click="playSample">
        <span>🔊</span>
        <span>听标准发音</span>
      </button>
    </div>

    <!-- 录音区域 -->
    <div class="record-section">
      <!-- 录音状态提示 -->
      <div class="record-status" :class="recordStatus">
        <div class="status-icon">
          <span v-if="recordStatus === 'idle'">🎤</span>
          <span v-if="recordStatus === 'recording'" class="recording-icon">🔴</span>
          <span v-if="recordStatus === 'processing'">⏳</span>
          <span v-if="recordStatus === 'done'">✅</span>
        </div>
        <p class="status-text">{{ statusText }}</p>
      </div>

      <!-- 录音按钮 -->
      <div class="record-controls">
        <button
          v-if="recordStatus === 'idle' || recordStatus === 'done'"
          class="record-btn main-btn"
          :class="{ restart: recordStatus === 'done' }"
          @click="startRecord"
        >
          <span class="btn-inner">
            <span class="mic-icon">🎤</span>
          </span>
        </button>

        <button
          v-if="recordStatus === 'recording'"
          class="stop-btn main-btn"
          @click="stopRecord"
        >
          <span class="btn-inner">
            <span class="stop-icon">⏹️</span>
          </span>
        </button>

        <!-- 播放录音按钮 -->
        <button
          v-if="recordStatus === 'done' && lastRecordId"
          class="replay-btn"
          @click="startReplay"
        >
          <span>🔊 播放我的录音</span>
        </button>
      </div>

      <!-- 音波动画 -->
      <div v-if="recordStatus === 'recording'" class="sound-waves">
        <div class="wave"></div>
        <div class="wave"></div>
        <div class="wave"></div>
        <div class="wave"></div>
        <div class="wave"></div>
      </div>
    </div>

    <!-- 评分结果 -->
    <div v-if="scoreResult" class="score-section">
      <div class="score-card">
        <div class="score-header">
          <h3>评测结果</h3>
          <div class="overall-score" :style="{ color: getScoreColor(scoreResult.overall) }">
            <span class="score-number">{{ scoreResult.overall }}</span>
            <span class="score-unit">分</span>
          </div>
        </div>

        <!-- 音素详情 -->
        <div v-if="scoreResult.phonemes && scoreResult.phonemes.length > 0" class="phonemes-detail">
          <h4>音素分析</h4>
          <div class="phonemes-list">
            <div
              v-for="(phoneme, index) in scoreResult.phonemes"
              :key="index"
              class="phoneme-item"
              :class="getPhonemeClass(phoneme.score)"
            >
              <span class="phoneme-char">{{ phoneme.phoneme }}</span>
              <span class="phoneme-score">{{ phoneme.score }}</span>
            </div>
          </div>
        </div>

        <!-- 建议 -->
        <div v-if="scoreResult.suggestion" class="suggestion">
          <h4>改进建议</h4>
          <p>{{ scoreResult.suggestion }}</p>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <button class="action-btn secondary" @click="goBack">
          返回列表
        </button>
        <button class="action-btn primary" @click="startRecord">
          再练一次
        </button>
      </div>
    </div>

    <!-- 学习小贴士 -->
    <div v-if="!scoreResult" class="tips-section">
      <div class="tips-card">
        <span class="tips-icon">💡</span>
        <p class="tips-text">{{ currentTip }}</p>
      </div>
    </div>

    <!-- 装饰 -->
    <div class="decoration">
      <div class="music-note note-1">🎵</div>
      <div class="music-note note-2">🎶</div>
      <div class="sparkle">✨</div>
    </div>
  </div>
</template>

<script>
import { _17kouyu } from '@/components/iload_es'
import { getWordDetail, saveScore } from '@/api/book'
import { ensureWechatOpenid } from '@/utils/wechat-auth'

const Config = {
  appKey: '17769972980006ff',
  secretKey: '2f5b9a61269e1ec717a331cf8a8846eb'
}

export default {
  name: 'RecordPractice',
  data() {
    return {
      word: {},
      bookId: null,
      wordId: null,
      user: null,
      recorder: null,
      lastRecordId: '',
      recordStatus: 'idle', // idle, recording, processing, done
      scoreResult: null,
      tips: [
        '练习时保持环境安静，录音更清晰',
        '注意单词的重音位置',
        '可以先把单词拆分成音节来练习',
        '模仿标准发音的语调和节奏',
        '多练习几次，会有进步的！'
      ],
      loading: false
    }
  },
  computed: {
    statusText() {
      const texts = {
        idle: '点击按钮开始录音',
        recording: '正在录音...请朗读单词',
        processing: '正在评测中...',
        done: '评测完成！'
      }
      return texts[this.recordStatus]
    },
    currentTip() {
      return this.tips[Math.floor(Math.random() * this.tips.length)]
    },
    wordIcon() {
      // 根据单词返回对应的emoji
      const icons = {
        hen: '🐔',
        wheat: '🌾',
        seed: '🌱',
        plant: '🌿',
        cat: '🐱',
        dog: '🐶',
        pig: '🐷',
        house: '🏠',
        straw: '🌾',
        stick: '🌿',
        brick: '🧱',
        duck: '🦆',
        egg: '🥚',
        swan: '🦢',
        princess: '👸',
        fairy: '🧚',
        glass: '🥃',
        shoe: '👠'
      }
      return icons[this.word.word] || '📖'
    }
  },
  created() {
    this.bookId = this.$route.params.id
    this.wordId = this.$route.params.wordId
    this.fetchWordDetail()
    this.ensureWechatUser()
  },
  mounted() {
    this.initRecorder()
  },
  beforeDestroy() {
    if (this.recorder) {
      this.recorder = null
    }
  },
  methods: {
    async fetchWordDetail() {
      this.loading = true
      try {
        const res = await getWordDetail(this.wordId)
        this.word = res.data || { word: 'hen', phonetic: '/hen/', meaning: '母鸡' }
      } catch (error) {
        console.error('获取单词详情失败:', error)
        // 使用默认数据
        const wordsData = {
          1: { id: 1, word: 'hen', phonetic: '/hen/', meaning: '母鸡' },
          2: { id: 2, word: 'wheat', phonetic: '/wiːt/', meaning: '小麦' },
          3: { id: 3, word: 'seed', phonetic: '/siːd/', meaning: '种子' },
          4: { id: 4, word: 'plant', phonetic: '/plænt/', meaning: '种植' },
          5: { id: 5, word: 'cat', phonetic: '/kæt/', meaning: '猫' },
          6: { id: 6, word: 'dog', phonetic: '/dɒg/', meaning: '狗' },
          7: { id: 7, word: 'pig', phonetic: '/pɪg/', meaning: '猪' },
          8: { id: 8, word: 'house', phonetic: '/haʊs/', meaning: '房子' },
          9: { id: 9, word: 'straw', phonetic: '/strɔː/', meaning: '稻草' }
        }
        this.word = wordsData[this.wordId] || { word: 'hen', phonetic: '/hen/', meaning: '母鸡' }
      } finally {
        this.loading = false
      }
    },
    initRecorder() {
      if (this.recorder) return

      try {
        this.recorder = new _17kouyu.IRecorder({
          id: 'iRecorder',
          mode: 2,
          debug: true,
          appKey: Config.appKey,
          secretKey: Config.secretKey,
          onFlashLoad: (code, message) => {
            if (code === 50000) {
              console.log('录音机加载成功')
            }
          },
          onConnectorStatusChange: (code, message) => {
            if (code === '50100') {
              console.log('连接录音服务器成功')
            }
          },
          onMicStatusChange: (code, message) => {
            if (code === '50001') {
              console.log('可以访问麦克风')
            } else if (code === '50002') {
              alert('无法访问麦克风，请检查权限设置')
            }
          },
          onError: (message) => {
            console.error(message)
            alert('网络繁忙，请重试！')
            this.recordStatus = 'idle'
          },
          onScore: (message) => {
            console.log('评分结果:', message)
            this.handleScoreResult(message)
          }
        })
      } catch (error) {
        console.error('初始化录音失败:', error)
      }
    },
    startRecord() {
      if (!this.recorder || !this.recorder.canRecord) {
        alert('录音机尚未准备好，请稍后再试')
        return
      }

      this.scoreResult = null
      this.lastRecordId = ''
      this.recordStatus = 'recording'

      this.recorder.record({
        duration: 5000,
        serverParams: {
          coreType: 'word.eval',
          refText: this.word.word,
          userId: 'guest',
          attachAudioUrl: 1
        },
        onRecordIdGenerated: (code, message) => {
          this.lastRecordId = message.recordId
        },
        onStart: () => {
          console.log('录音开始')
        },
        onStop: () => {
          console.log('录音自动停止')
          this.recordStatus = 'processing'
        }
      })

      // 5秒后自动停止（如果没手动停止）
      setTimeout(() => {
        if (this.recordStatus === 'recording') {
          this.stopRecord()
        }
      }, 5000)
    },
    stopRecord() {
      if (!this.recorder) return

      this.recorder.stop({
        onStop: () => {
          console.log('录音手动停止')
          this.recordStatus = 'processing'
        }
      })
    },
    handleScoreResult(message) {
      let result
      try {
        const messageObj = typeof message === 'string' ? JSON.parse(message) : message
        if (messageObj.result) {
          result = {
            overall: Math.round(messageObj.result.overall || 0),
            phonemes: this.parsePhonemes(messageObj),
            suggestion: this.generateSuggestion(messageObj.result.overall)
          }
        } else {
          result = {
            overall: 0,
            phonemes: [],
            suggestion: '网络繁忙，请重试！'
          }
        }
      } catch (e) {
        result = {
          overall: 0,
          phonemes: [],
          suggestion: '网络繁忙，请重试！'
        }
      }

      this.scoreResult = result
      this.recordStatus = 'done'

      // 保存学习记录
      this.saveStudyRecord(result)
    },
    parsePhonemes(messageObj) {
      // 解析音素分数
      const phonemes = []
      if (messageObj.result && messageObj.result.details) {
        // 根据实际API返回格式解析
        const details = messageObj.result.details
        // 这里需要根据实际返回结构调整
      }
      // 如果没有详细音素，生成模拟数据
      const letters = this.word.word.split('')
      letters.forEach(letter => {
        phonemes.push({
          phoneme: letter,
          score: Math.floor(Math.random() * 30) + 70
        })
      })
      return phonemes
    },
    generateSuggestion(overall) {
      if (overall >= 90) {
        return '太棒了！发音非常标准，继续保持！'
      } else if (overall >= 70) {
        return '发音很好，还有一些进步空间，多练习！'
      } else if (overall >= 50) {
        return '发音还可以，注意听标准发音后再试一次'
      } else {
        return '要多加练习哦，先听几遍标准发音'
      }
    },
    async ensureWechatUser() {
      try {
        this.user = await ensureWechatOpenid(this.$route)
      } catch (error) {
        console.error('获取微信用户身份失败:', error)
      }
    },
    async saveStudyRecord(result) {
      const records = JSON.parse(localStorage.getItem('studyRecords') || '[]')
      const record = {
        id: Date.now().toString(),
        userId: this.user ? this.user.openid : '',
        bookId: this.bookId,
        bookTitle: this.getBookTitle(this.bookId),
        wordId: this.wordId,
        word: this.word.word,
        score: result.overall,
        recordId: this.lastRecordId,
        recordUrl: '',
        createTime: new Date().toLocaleString(),
        synced: false
      }
      records.push(record)
      localStorage.setItem('studyRecords', JSON.stringify(records))

      try {
        if (!this.user) {
          await this.ensureWechatUser()
        }
        await saveScore({
          bookId: Number(this.bookId),
          wordId: Number(this.wordId),
          word: this.word.word,
          score: result.overall,
          recordId: this.lastRecordId
        })
        record.synced = true
        localStorage.setItem('studyRecords', JSON.stringify(records))
      } catch (error) {
        console.error('保存评分到后端失败:', error)
      }
    },
    getBookTitle(bookId) {
      const titles = { 1: 'The Little Red Hen', 2: 'Three Little Pigs' }
      return titles[bookId] || 'Unknown Book'
    },
    startReplay() {
      if (!this.recorder || !this.lastRecordId) return

      this.recorder.startReplay({
        recordId: this.lastRecordId,
        onStart: () => {
          console.log('回放开始')
        },
        onStop: () => {
          console.log('回放结束')
        }
      })
    },
    playSample() {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(this.word.word)
        utterance.lang = 'en-US'
        utterance.rate = 0.8
        window.speechSynthesis.speak(utterance)
      }
    },
    getScoreColor(score) {
      if (score >= 90) return '#4CAF50'
      if (score >= 70) return '#FF9800'
      return '#F44336'
    },
    getPhonemeClass(score) {
      if (score >= 80) return 'good'
      if (score >= 60) return 'medium'
      return 'poor'
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.record-practice-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #FFF9C4 0%, #FFECB3 100%);
  padding-bottom: 30px;
}

/* 头部 */
.header {
  display: flex;
  align-items: center;
  padding: 15px;
  position: relative;
}

.back-btn {
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.back-icon {
  font-size: 18px;
}

.page-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 18px;
  color: #333;
  margin: 0;
}

/* 单词展示卡片 */
.word-display-card {
  background: white;
  margin: 0 15px 20px;
  padding: 25px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
}

.word-image {
  font-size: 60px;
  margin-bottom: 10px;
}

.target-word {
  font-size: 36px;
  color: #333;
  margin: 0 0 8px;
  font-weight: 700;
  letter-spacing: 2px;
}

.word-phonetic {
  font-size: 16px;
  color: #FF6B6B;
  margin: 0 0 5px;
}

.word-meaning {
  font-size: 14px;
  color: #888;
  margin: 0 0 15px;
}

.play-sample-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #E3F2FD;
  border: none;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 13px;
  color: #1976D2;
  cursor: pointer;
}

/* 录音区域 */
.record-section {
  padding: 0 15px;
  text-align: center;
}

.record-status {
  margin-bottom: 20px;
}

.status-icon {
  font-size: 50px;
  margin-bottom: 10px;
}

.recording-icon {
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
}

.status-text {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.record-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.main-btn {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.record-btn {
  background: linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%);
  box-shadow: 0 6px 20px rgba(255,107,107,0.4);
}

.record-btn.restart {
  background: linear-gradient(135deg, #4ECDC4 0%, #6EE7DE 100%);
  box-shadow: 0 6px 20px rgba(78,205,196,0.4);
}

.stop-btn {
  background: linear-gradient(135deg, #FFB6C1 0%, #FFD1DC 100%);
  box-shadow: 0 6px 20px rgba(255,182,193,0.4);
}

.btn-inner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.mic-icon, .stop-icon {
  font-size: 40px;
}

.replay-btn {
  background: white;
  border: 2px solid #4ECDC4;
  padding: 12px 25px;
  border-radius: 25px;
  font-size: 14px;
  color: #4ECDC4;
  cursor: pointer;
}

/* 音波动画 */
.sound-waves {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  height: 40px;
}

.wave {
  width: 6px;
  height: 20px;
  background: #FF6B6B;
  border-radius: 3px;
  animation: wave 0.5s ease-in-out infinite alternate;
}

.wave:nth-child(2) { animation-delay: 0.1s; height: 30px; }
.wave:nth-child(3) { animation-delay: 0.2s; height: 40px; }
.wave:nth-child(4) { animation-delay: 0.3s; height: 30px; }
.wave:nth-child(5) { animation-delay: 0.4s; }

@keyframes wave {
  from { transform: scaleY(0.5); opacity: 0.5; }
  to { transform: scaleY(1); opacity: 1; }
}

/* 评分区域 */
.score-section {
  margin-top: 25px;
  padding: 0 15px;
}

.score-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
}

.score-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.score-header h3 {
  font-size: 16px;
  color: #333;
  margin: 0;
}

.overall-score {
  display: flex;
  align-items: baseline;
}

.score-number {
  font-size: 36px;
  font-weight: 700;
}

.score-unit {
  font-size: 14px;
  margin-left: 3px;
}

/* 音素详情 */
.phonemes-detail {
  margin-bottom: 20px;
}

.phonemes-detail h4 {
  font-size: 14px;
  color: #666;
  margin: 0 0 12px;
}

.phonemes-list {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.phoneme-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 12px;
  background: #f5f5f5;
  border-radius: 10px;
  min-width: 40px;
}

.phoneme-item.good {
  background: #E8F5E9;
  color: #2E7D32;
}

.phoneme-item.medium {
  background: #FFF3E0;
  color: #EF6C00;
}

.phoneme-item.poor {
  background: #FFEBEE;
  color: #C62828;
}

.phoneme-char {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 3px;
}

.phoneme-score {
  font-size: 12px;
}

/* 建议 */
.suggestion {
  padding: 15px;
  background: #FFF8E1;
  border-radius: 12px;
}

.suggestion h4 {
  font-size: 14px;
  color: #F9A825;
  margin: 0 0 8px;
}

.suggestion p {
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 15px;
  margin-top: 15px;
}

.action-btn {
  flex: 1;
  padding: 14px;
  border: none;
  border-radius: 25px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.action-btn.primary {
  background: #FF6B6B;
  color: white;
  box-shadow: 0 4px 15px rgba(255,107,107,0.3);
}

.action-btn.secondary {
  background: white;
  color: #666;
  border: 1px solid #ddd;
}

/* 小贴士 */
.tips-section {
  padding: 0 15px;
  margin-top: 30px;
}

.tips-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.tips-icon {
  font-size: 24px;
}

.tips-text {
  flex: 1;
  font-size: 13px;
  color: #666;
  margin: 0;
}

/* 装饰 */
.music-note {
  position: fixed;
  font-size: 24px;
  opacity: 0.3;
  animation: float-note 3s ease-in-out infinite;
}

.note-1 {
  top: 150px;
  left: 20px;
}

.note-2 {
  bottom: 200px;
  right: 20px;
  animation-delay: 1s;
}

.sparkle {
  position: fixed;
  bottom: 100px;
  left: 30px;
  font-size: 20px;
  opacity: 0.4;
  animation: twinkle 2s ease-in-out infinite;
}

@keyframes float-note {
  0%, 100% { transform: translateY(0) rotate(-10deg); }
  50% { transform: translateY(-15px) rotate(10deg); }
}

@keyframes twinkle {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 0.6; }
}
</style>



// WEBPACK FOOTER //
// src/views/RecordPractice.vue